import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3V3 } from '../contexts/Web3ContextV3';
import toast from 'react-hot-toast';

/**
 * V3 NFT Minting Page - Black & White Premium Theme
 * Random Minting / Custom Number + Personal Signature
 */
const MintNFTV3 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    account,
    systemConfig,
    balances,
    nftStats,
    connectWallet,
    isConnecting,
    mintRandomUSDT,
    mintRandomETH,
    mintWithNumberUSDT,
    mintWithNumberETH,
    refreshBalances,
    refreshSystemConfig
  } = useWeb3V3();

  // Minting mode: 'random' | 'custom'
  const [mintMode, setMintMode] = useState('random');

  // Payment method: 'USDT' | 'ETH'
  const [paymentMode, setPaymentMode] = useState('USDT');

  // Custom number
  const [customNumber, setCustomNumber] = useState('');

  // Personal signature
  const [signature, setSignature] = useState('');

  // Minting state
  const [minting, setMinting] = useState(false);

  // Preview NFT number (shown in random mode)
  const [previewNumber, setPreviewNumber] = useState('');

  // Generate preview number on component load
  useEffect(() => {
    generatePreviewNumber();
  }, []);

  // Generate random preview number
  const generatePreviewNumber = () => {
    const random = Math.floor(Math.random() * 100000);
    setPreviewNumber(random.toString().padStart(6, '0'));
  };

  // Handle number input
  const handleNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow numbers
    if (value.length <= 6) {
      setCustomNumber(value);
    }
  };

  // Handle signature input
  const handleSignatureChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) { // Limit signature length
      setSignature(value);
    }
  };

  // Quick fill random number
  const handleQuickRandom = () => {
    const random = Math.floor(Math.random() * 100000);
    setCustomNumber(random.toString().padStart(6, '0'));
  };

  // Execute minting
  const handleMint = async () => {
    if (!account) {
      toast.error(t('mint.error.connectWallet'));
      connectWallet();
      return;
    }

    // Validate input
    if (mintMode === 'custom') {
      if (!customNumber || customNumber.length !== 6) {
        toast.error(t('mint.error.noNumber'));
        return;
      }

      const numberValue = parseInt(customNumber);
      if (numberValue < 0 || numberValue > 99999) {
        toast.error(t('mint.error.invalidRange'));
        return;
      }
    }

    setMinting(true);

    try {
      let receipt;

      if (mintMode === 'random') {
        // Random minting
        if (paymentMode === 'USDT') {
          receipt = await mintRandomUSDT(signature);
        } else {
          receipt = await mintRandomETH(signature);
        }
      } else {
        // Custom number minting
        const number = parseInt(customNumber);
        if (paymentMode === 'USDT') {
          receipt = await mintWithNumberUSDT(number, signature);
        } else {
          receipt = await mintWithNumberETH(number, signature);
        }
      }

      // Minting successful
      if (receipt) {
        // Refresh data
        await Promise.all([
          refreshBalances(),
          refreshSystemConfig()
        ]);

        // Reset form
        setCustomNumber('');
        setSignature('');
        generatePreviewNumber();

        // Navigate to my assets page after 3 seconds
        setTimeout(() => {
          navigate('/my-assets');
        }, 3000);
      }
    } catch (error) {
      console.error('Minting failed:', error);
      // Toast already handled in context
    } finally {
      setMinting(false);
    }
  };

  // Get current price
  const getCurrentPrice = () => {
    return paymentMode === 'USDT' ? systemConfig.usdtPrice : systemConfig.ethPrice;
  };

  // Check if balance is sufficient
  const hasEnoughBalance = () => {
    const price = parseFloat(getCurrentPrice());
    const balance = paymentMode === 'USDT'
      ? parseFloat(balances.usdt)
      : parseFloat(balances.eth);
    return balance >= price;
  };

  // Remaining NFT count
  const remainingNFTs = () => {
    const total = parseInt(nftStats.totalSupply || '100000');
    const minted = parseInt(nftStats.totalMinted || '0');
    return total - minted;
  };

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container-v3">
        {/* Page title */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4">{t('mint.title')}</h1>
          <p className="text-xl text-gray-400">
            {t('mint.subtitle')}
          </p>
        </div>

        {/* Remaining count notice */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="card p-4 flex items-center justify-between">
            <div className="text-gray-400">
              <span className="text-sm uppercase tracking-wider">{t('mint.remaining')}：</span>
              <span className="ml-2 text-2xl font-bold font-mono text-white">
                {remainingNFTs().toLocaleString()}
              </span>
              <span className="ml-1 text-gray-600">/ {nftStats.totalSupply}</span>
            </div>
            <div className="text-gray-500 text-sm">
              {t('mint.currentPeriod')}: #{nftStats.currentDrawId}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left: Configuration area */}
            <div className="space-y-6">
              {/* Minting mode selection */}
              <div className="card p-6">
                <h3 className="text-xl font-bold mb-4">{t('mint.mode')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setMintMode('random')}
                    className={`p-4 border-2 transition-all ${
                      mintMode === 'random'
                        ? 'border-white bg-white text-black'
                        : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="font-bold">{t('mint.random.title')}</div>
                    <div className="text-xs mt-1 opacity-70">{t('mint.random.desc')}</div>
                  </button>

                  <button
                    onClick={() => setMintMode('custom')}
                    className={`p-4 border-2 transition-all ${
                      mintMode === 'custom'
                        ? 'border-white bg-white text-black'
                        : 'border-gray-700 hover:border-gray-500'
                    }`}
                  >
                    <div className="font-bold">{t('mint.custom.title')}</div>
                    <div className="text-xs mt-1 opacity-70">{t('mint.custom.desc')}</div>
                  </button>
                </div>
              </div>

              {/* Number input (custom mode) */}
              {mintMode === 'custom' && (
                <div className="card p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold">{t('mint.custom.label')}</h3>
                    <button
                      onClick={handleQuickRandom}
                      className="btn-ghost px-3 py-1 text-sm"
                    >
                      {t('mint.random.button')}
                    </button>
                  </div>

                  <input
                    type="text"
                    value={customNumber}
                    onChange={handleNumberChange}
                    placeholder={t('mint.custom.placeholder')}
                    className="input text-center text-3xl font-mono tracking-widest"
                    maxLength={6}
                  />

                  <div className="mt-2 text-center text-sm text-gray-500">
                    {t('mint.custom.instruction')}
                  </div>
                </div>
              )}

              {/* Personal signature */}
              <div className="card p-6">
                <h3 className="text-xl font-bold mb-4">{t('mint.signature.label')} <span className="text-sm text-gray-500 font-normal">{t('mint.signature.optional')}</span></h3>

                <textarea
                  value={signature}
                  onChange={handleSignatureChange}
                  placeholder={t('mint.signature.placeholder')}
                  className="input min-h-[100px] resize-none"
                  maxLength={50}
                />

                <div className="mt-2 text-right text-xs text-gray-500">
                  {signature.length} / 50
                </div>
              </div>

              {/* Payment method selection */}
              <div className="card p-6">
                <h3 className="text-xl font-bold mb-4">{t('mint.payment.title')}</h3>

                <div className="space-y-3">
                  {/* USDT */}
                  {systemConfig.usdtEnabled && (
                    <button
                      onClick={() => setPaymentMode('USDT')}
                      className={`w-full p-4 border-2 transition-all flex items-center justify-between ${
                        paymentMode === 'USDT'
                          ? 'border-white bg-gray-900'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-2xl"></div>
                        <div className="text-left">
                          <div className="font-bold">{t('mint.payment.usdt')}</div>
                          <div className="text-sm text-gray-400">
                            {t('mint.balance')}: {balances.usdt}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold font-mono">
                          {systemConfig.usdtPrice}
                        </div>
                        <div className="text-xs text-gray-500">USDT</div>
                      </div>
                    </button>
                  )}

                  {/* ETH */}
                  {systemConfig.ethEnabled && (
                    <button
                      onClick={() => setPaymentMode('ETH')}
                      className={`w-full p-4 border-2 transition-all flex items-center justify-between ${
                        paymentMode === 'ETH'
                          ? 'border-white bg-gray-900'
                          : 'border-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-2xl"></div>
                        <div className="text-left">
                          <div className="font-bold">{t('mint.payment.eth')}</div>
                          <div className="text-sm text-gray-400">
                            {t('mint.balance')}: {parseFloat(balances.eth).toFixed(4)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xl font-bold font-mono">
                          {systemConfig.ethPrice}
                        </div>
                        <div className="text-xs text-gray-500">ETH</div>
                      </div>
                    </button>
                  )}
                </div>

                {/* Insufficient balance notice */}
                {account && !hasEnoughBalance() && (
                  <div className="mt-4 p-3 bg-gray-900 border border-gray-700 text-sm text-gray-400">
                    {t('mint.payment.insufficientBalance')} {paymentMode}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Preview and confirm */}
            <div className="space-y-6">
              {/* NFT preview card */}
              <div className="card p-8">
                <div className="text-center mb-6">
                  <div className="text-sm text-gray-500 uppercase tracking-wider mb-2">
                    {t('mint.preview.title')}
                  </div>
                  <div className="text-6xl font-black font-mono tracking-wider mb-4">
                    {mintMode === 'random'
                      ? previewNumber
                      : (customNumber || '------').padStart(6, '0')
                    }
                  </div>
                  {mintMode === 'random' && (
                    <div className="text-xs text-gray-600">
                      {t('mint.preview.actualNumber')}
                    </div>
                  )}
                </div>

                <div className="divider"></div>

                {/* Signature preview */}
                <div className="mt-6">
                  <div className="text-sm text-gray-500 mb-2">{t('mint.signature.label')}</div>
                  <div className="min-h-[60px] p-3 bg-black border border-gray-800 text-gray-300 italic">
                    {signature || <span className="text-gray-700">{t('mint.signature.none')}</span>}
                  </div>
                </div>

                {/* Metadata */}
                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('mint.preview.mode')}</span>
                    <span className="font-mono">
                      {mintMode === 'random' ? t('mint.random.title') : t('mint.custom.title')}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('mint.preview.paymentMethod')}</span>
                    <span className="font-mono">{paymentMode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('mint.price')}</span>
                    <span className="font-mono font-bold">
                      {getCurrentPrice()} {paymentMode}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t('mint.preview.participateDraw')}</span>
                    <span className="font-mono">#{nftStats.currentDrawId}</span>
                  </div>
                </div>
              </div>

              {/* Fee breakdown */}
              <div className="card p-6">
                <h3 className="text-lg font-bold mb-4">{t('mint.feeDistribution.title')}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('mint.feeDistribution.operationFee')}</span>
                    <span className="font-mono">{t('mint.feeDistribution.operationWallet')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('mint.feeDistribution.promotionFee')}</span>
                    <span className="font-mono">{t('mint.feeDistribution.promotionWallet')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('mint.feeDistribution.dividendPool')}</span>
                    <span className="font-mono">{t('mint.feeDistribution.nftHolders')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">{t('mint.feeDistribution.prizePool')}</span>
                    <span className="font-mono">{t('mint.feeDistribution.winnings')}</span>
                  </div>
                </div>
              </div>

              {/* Mint button */}
              <div className="space-y-4">
                {!account ? (
                  <button
                    onClick={connectWallet}
                    disabled={isConnecting}
                    className="btn-primary w-full py-4 text-lg"
                  >
                    {isConnecting ? t('common.connecting') : t('common.connectWallet')}
                  </button>
                ) : (
                  <button
                    onClick={handleMint}
                    disabled={minting || !hasEnoughBalance() || (mintMode === 'custom' && customNumber.length !== 6)}
                    className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {minting ? t('mint.minting') : t('mint.confirm')}
                  </button>
                )}

                <button
                  onClick={() => navigate('/')}
                  className="btn-outline w-full py-3"
                >
                  {t('mint.backHome')}
                </button>
              </div>

              {/* Tips */}
              <div className="text-xs text-gray-600 text-center space-y-1">
                <p>{t('mint.tips.tip1')}</p>
                <p>{t('mint.tips.tip2')}</p>
                <p>{t('mint.tips.tip3')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Winning rules explanation */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="card p-8">
            <h3 className="text-2xl font-bold mb-6 text-center">{t('mint.rules.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="text-center p-4 border border-gray-800">
                <div className="font-bold mb-1">{t('mint.rules.grand')}</div>
                <div className="text-2xl font-mono text-white mb-2">40%</div>
                <div className="text-xs text-gray-500">{t('mint.rules.match6')}</div>
              </div>
              <div className="text-center p-4 border border-gray-800">
                <div className="font-bold mb-1">{t('mint.rules.first')}</div>
                <div className="text-2xl font-mono text-white mb-2">30%</div>
                <div className="text-xs text-gray-500">{t('mint.rules.match5')}</div>
              </div>
              <div className="text-center p-4 border border-gray-800">
                <div className="font-bold mb-1">{t('mint.rules.second')}</div>
                <div className="text-2xl font-mono text-white mb-2">20%</div>
                <div className="text-xs text-gray-500">{t('mint.rules.match4')}</div>
              </div>
              <div className="text-center p-4 border border-gray-800">
                <div className="font-bold mb-1">{t('mint.rules.third')}</div>
                <div className="text-2xl font-mono text-white mb-2">10%</div>
                <div className="text-xs text-gray-500">{t('mint.rules.match3')}</div>
              </div>
            </div>
            <div className="text-center mt-6 text-sm text-gray-500">
              {t('mint.rules.instruction')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MintNFTV3;
